import styles from './change-course-users-password-dialog.module.scss';
import { ValueFormatterParams } from 'ag-grid-community';
import { addDays } from 'date-fns/esm';
import { useState } from 'react';
import { useGetRequestNewHook, useMakeRequestHook } from '../requestUtils';
import { Col, Row } from 'reactstrap';
import { booleanHeader, Checkbox, CreateHeaders, dateHeader, DatePicker, HeaderSizeEnum, SecondaryButton, StepDialog, TextField, textHeader } from '@frontend-workspace/react-shared-lib';
import { setHours } from 'date-fns';
import WebkuaTable from '../webkua-table/webkua-table';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface ChangeCourseUsersPasswordDialogProps {
  onClose: () => void;
}

export function ChangeCourseUsersPasswordDialog(props: ChangeCourseUsersPasswordDialogProps) {
  const makeRequest = useMakeRequestHook();
  const [activeStep, setActiveStep] = useState<number>(0);
  const generateRandomPassord = () => (Math.random() + 1).toString(36).substring(2);
  const [password, setPassword] = useState<string>(generateRandomPassord());
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [courseName, setCourseName] = useState<string>("");
  const [courseEndDate, setCourseEndDate] = useState<Date>(addDays(new Date(), 1));
  const { t } = useTranslation();

  const createNewPassword = () => setPassword(generateRandomPassord());

  const handleFinish = () => {
    const body = {
      emails: selectedEmails,
      newPassword: password,
      endDate: courseEndDate,
      courseName: courseName
    };
    makeRequest(`courses/action/initializeCoursePassword`, 'PUT', body).then(result => props.onClose());
  };

  return (
    <StepDialog
      activeStep={activeStep}
      setActiveStep={setActiveStep}
      onClose={props.onClose}
      onFinish={handleFinish}
      steps={[t("changeCourseUsersPassword.selectPassword"), t("changeCourseUsersPassword.selectEmails")]}
      title={t("changeCourseUsersPassword.changePasswordsForCourseUsers")}
    >
      {activeStep === 0 && <BaseInfoStep
        courseName={courseName}
        password={password}
        courseEndDate={courseEndDate}
        setEndDate={setCourseEndDate}
        generateRandomPassord={createNewPassword}
        setCourseName={setCourseName}
        setPassword={setPassword}
      />}
      {activeStep === 1 && <ChooseAccountsStep
        selectedEmails={selectedEmails}
        setSelectedEmails={setSelectedEmails}
      />}
    </StepDialog>
  );
}

function BaseInfoStep(props: {
  courseName: string,
  courseEndDate: Date,
  password: string,
  setCourseName: (name: string) => void,
  setEndDate: (endDate: Date) => void,
  generateRandomPassord: () => void,
  setPassword: (password: string) => void
}) {
  const { t } = useTranslation();
  const handleChangePassword = (event: React.ChangeEvent<HTMLInputElement>) => props.setPassword(event.target.value);
  const handleChangeCourseName = (event: React.ChangeEvent<HTMLInputElement>) => props.setCourseName(event.target.value);
  const handleSetEndDate = (date: Date) => {
    //Todo:: hack to handle timezones. 
    const newDate = setHours(date, 3);
    props.setEndDate(newDate);
  }
  return <>
    <div className="d-flex flex-column">
      <div className='mb-3'>
        <Row>
          <Col xl={2}>
            <DatePicker
              inputFormat="dd.MM.yyyy"
              onChange={handleSetEndDate}
              label={t("changeCourseUsersPassword.untilDate")}
              value={props.courseEndDate}
              views={['day']}
            />
          </Col>
        </Row>
      </div>
      <div className='mb-3'>
        <TextField
          value={props.courseName}
          label={t("changeCourseUsersPassword.courseName")}
          fullWidth
          onChange={handleChangeCourseName}
          placeholder={""}
          name='courseName'
        />
      </div>
    </div>
    <div className="d-flex flex-row align-items-end flex-wrap mb-3">
      <div className='me-2'>
        <TextField
          value={props.password}
          label={t("changeCourseUsersPassword.passwordForCourseUsers")}
          name='password'
          onChange={handleChangePassword}
        />
      </div>
      <div className='mt-2 mt-sm-0'>
        <SecondaryButton
          variant='outlined'
          onClick={props.generateRandomPassord}>
          {t("changeCourseUsersPassword.generateNewPassword")}
        </SecondaryButton>
      </div>
    </div>
  </>;
}

function ChooseAccountsStep(props: {
  selectedEmails: string[],
  setSelectedEmails: (emails: string[]) => void
}) {
  const { data } = useGetRequestNewHook('courses/courseEmails/onlyActive/false', [], []);
  const { t } = useTranslation();

  const selectEmailRenderer = (params: ValueFormatterParams) => {
    const isSelected = props.selectedEmails.includes(params.value);
    const toggleEmail = () => {
      if (isSelected) {
        props.setSelectedEmails(props.selectedEmails.filter(email => email !== params.value))
      } else {
        props.setSelectedEmails([...props.selectedEmails, params.value])
      }
    };
    return <Checkbox
      checked={isSelected}
      onChange={toggleEmail}
    />
  }

  return (
    <WebkuaTable
      headers={CreateHeaders([
        textHeader("email", t("standard.email")),
        textHeader("firstName", t("standard.firstName")),
        textHeader("lastName", t("standard.lastName")),
        dateHeader("usedFrom", t("changeCourseUsersPassword.usedFrom")),
        dateHeader("usedUntil", t("changeCourseUsersPassword.usedUntil")),
        booleanHeader("isUsed", t("changeCourseUsersPassword.inUse")),
        textHeader("email", t("changeCourseUsersPassword.useEmail"), HeaderSizeEnum.SMALL, { renderer: selectEmailRenderer })
      ])}
      data={data}
    />
  );
}

export default ChangeCourseUsersPasswordDialog;